/**
 * On-behalf code scanning (Req 12.5, 13, 28.2–28.4). Every scan, camera or typed, goes
 * through the offline queue: online it is submitted at once and the result shown here;
 * offline it waits in IndexedDB and is sent on reconnect (review on the Queue screen).
 */
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { format } from '@macksons/shortcode';
import { useScanQueue, useT } from '../hooks';
import type { QueuedScan } from '../offline/scan-queue';
import { checkTypedCode, classifyScan } from '../scanner/inputs';
import { scanMismatchText } from '../scanner/messages';
import { useServices } from '../services';
import type { MemberLookupResult } from '../types';
import { CheckIcon, ClockIcon, CrossIcon } from './icons';
import { QrScanner } from './QrScanner';
import { ResultCard, useOnline, useProblemText, type Feedback } from './ui';

/** Short status line of a queued scan. */
export function useScanStatusText(): (item: QueuedScan, online: boolean) => string {
  const t = useT();
  const problemText = useProblemText();
  return (item, online) => {
    switch (item.status) {
      case 'done':
        return item.result?.reason === 'no_matching_rule'
          ? t('errors.no_matching_rule')
          : t('operator.claim.status.done', { count: Number(item.result?.points ?? 0) });
      case 'rejected':
        return problemText(item.problem?.code ?? 'internal_error');
      case 'retrying':
        return item.problem ? `${t('operator.claim.status.retrying')} ${problemText(item.problem.code)}` : t('operator.claim.status.retrying');
      case 'submitting':
        return t('operator.claim.status.submitting');
      default:
        return online ? t('operator.claim.status.queued') : t('scan.queued');
    }
  };
}

/** Status icon for a queued scan (icon + text, never colour alone). */
function StatusIcon({ status }: { status: QueuedScan['status'] }) {
  if (status === 'done') return <CheckIcon className="list__status-icon" />;
  if (status === 'rejected') return <CrossIcon className="list__status-icon" />;
  return <ClockIcon className="list__status-icon" />;
}

export function ClaimPanel({ member, active, onAvailable }: { member: MemberLookupResult; active: boolean; onAvailable: (available: string) => void }) {
  const t = useT();
  const { scanQueue, config } = useServices();
  const queue = useScanQueue();
  const online = useOnline();
  const statusText = useScanStatusText();
  const inputId = useId();
  const hintId = useId();
  const checkId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [feedback, setFeedback] = useState<Feedback>();
  /** Keys of the scans taken in this panel, newest first. */
  const [keys, setKeys] = useState<string[]>([]);
  const check = checkTypedCode(value);
  const mine = useMemo(() => {
    const byKey = new Map(queue.items.map((i) => [i.idempotencyKey, i]));
    return keys.flatMap((k) => {
      const item = byKey.get(k);
      return item ? [item] : [];
    });
  }, [queue.items, keys]);
  // Announce the outcome of the newest scan and keep the shown balance current.
  const latest = mine[0];
  const latestText = latest ? `${format(latest.shortCode)}: ${statusText(latest, online)}` : undefined;
  const latestStatus = latest?.status;
  const latestPoints = latest?.status === 'done' && latest.result?.reason !== 'no_matching_rule' ? latest.result?.points : undefined;
  useEffect(() => {
    if (!latestText) return;
    setFeedback({ tone: latestStatus === 'rejected' ? 'error' : latestStatus === 'done' ? 'ok' : 'info', text: latestText });
  }, [latestText, latestStatus]);
  const seenDone = useRef(new Set<string>());
  useEffect(() => {
    for (const item of [...mine].reverse()) {
      if (item.status === 'done' && item.result && !seenDone.current.has(item.idempotencyKey)) {
        seenDone.current.add(item.idempotencyKey);
        onAvailable(item.result.balance.available);
      }
    }
  }, [mine, onAvailable]);

  const claim = async (code: string) => {
    if (!scanQueue) return;
    const display = format(code);
    const duplicate = mine.find((i) => i.shortCode === code && i.status !== 'rejected');
    if (duplicate) {
      setFeedback({ tone: 'error', text: t('operator.claim.duplicate', { code: display }) });
      return;
    }
    try {
      const item = await scanQueue.enqueue({ shortCode: code, memberId: member.memberId });
      setKeys((k) => [item.idempotencyKey, ...k]);
      // Online: submit now; offline the queue returns at once and keeps the item.
      void scanQueue.flush();
    } catch {
      setFeedback({ tone: 'error', text: t('errors.internal_error') });
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (check.state !== 'valid') {
      setFeedback({ tone: 'error', text: t('scan.typo') });
      inputRef.current?.focus();
      return;
    }
    void claim(check.code);
    setValue('');
    inputRef.current?.focus();
  };
  const scanNext = () => {
    setFeedback(undefined);
    setValue('');
    inputRef.current?.focus();
  };
  const onScan = (text: string) => {
    const scan = classifyScan(text, config.qrHost);
    if (scan.kind === 'product') void claim(scan.code);
    else setFeedback({ tone: 'error', text: scanMismatchText(t, scan, 'product') });
  };

  if (!scanQueue) {
    return <p className="notice notice--error">{t('operator.claim.noQueue')}</p>;
  }

  const points = latestPoints !== undefined ? t('common.points', { count: Number(latestPoints) }) : undefined;
  return (
    <div className="form-panel">
      <h3>{t('operator.claim.title')}</h3>
      <QrScanner onScan={onScan} continuous startLabel={t('operator.claim.scanCamera')} disabled={!active} />
      <ResultCard feedback={feedback} points={points} testId="claim" />
      {feedback && (
        <button type="button" className="button button--block" onClick={scanNext} data-testid="claim-next">
          {t('operator.claim.scanNext')}
        </button>
      )}
      <form onSubmit={submit} noValidate className="form">
        <div className="field">
          <label htmlFor={inputId}>{t('operator.claim.codeLabel')}</label>
          <p id={hintId} className="hint">
            {t('operator.claim.hint')}
          </p>
          <input
            ref={inputRef}
            id={inputId}
            className="input input--code"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('scan.codePlaceholder')}
            aria-describedby={`${hintId} ${checkId}`}
            aria-invalid={check.state === 'invalid'}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
          />
          {/* Live check (Req 11.3): only the verdict is announced, not every keystroke. */}
          <p id={checkId} className={`check check--${check.state}`} aria-live="polite" data-testid="code-check">
            {check.state === 'valid'
              ? t('operator.claim.valid', { code: check.display })
              : check.state === 'invalid'
                ? t('scan.typo')
                : check.state === 'incomplete'
                  ? t('operator.claim.remaining', { count: check.remaining })
                  : ''}
          </p>
        </div>
        <div className="sticky-action">
          <button type="submit" className="button button--primary button--block" disabled={check.state !== 'valid'}>
            {t('scan.submit')}
          </button>
          {check.state !== 'valid' && (
            <p className="hint" data-testid="claim-disabled-hint">
              {t('operator.claim.disabledHint')}
            </p>
          )}
        </div>
      </form>
      <h4>{t('operator.claim.recent')}</h4>
      {mine.length === 0 ? (
        <p className="hint">{t('operator.claim.none')}</p>
      ) : (
        <ul className="list" data-testid="claim-list">
          {mine.map((item) => (
            <li key={item.idempotencyKey} className={`list__item list__item--${item.status}`}>
              <span className="list__main">
                <StatusIcon status={item.status} />
                <span className="list__text">
                  <span className="code">{format(item.shortCode)}</span>
                  <span>{statusText(item, online)}</span>
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
